/* ───────────────────────────────────────────────────────────────────────────
   TextZoom — global UI zoom pill.

   Sits fixed at the bottom-right of every page. Three controls: A−, percent
   (click to reset to 100%), A+. Writes `zoom` on the document root so the
   ENTIRE website scales: text, layout, controls, modals — everything.

   Persisted in localStorage as `nmTextZoom` so a user's preference sticks
   across reloads and routes.

   Range: 70% – 160%, in 10% steps.
   ─────────────────────────────────────────────────────────────────────────── */

import { useEffect, useState } from "react";

const MIN = 0.7, MAX = 1.6, STEP = 0.1;

function clamp(z) {
  if (Number.isNaN(z)) return 1;
  if (z < MIN) return MIN;
  if (z > MAX) return MAX;
  return Math.round(z * 100) / 100;
}

export default function TextZoom() {
  const [zoom, setZoom] = useState(() => {
    const saved = Number(localStorage.getItem("nmTextZoom"));
    return saved >= MIN && saved <= MAX ? saved : 1;
  });
  const [hover, setHover] = useState(false);

  useEffect(() => {
    document.documentElement.style.zoom = String(zoom);
    localStorage.setItem("nmTextZoom", String(zoom));
    return () => { /* don't undo on unmount — preference is global */ };
  }, [zoom]);

  // Keyboard shortcuts: Ctrl/Cmd + plus/minus/0 (alongside the browser's own
  // zoom, but scoped to our doc styling).
  useEffect(() => {
    function onKey(e) {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key === "=" || e.key === "+") { setZoom(z => clamp(z + STEP)); e.preventDefault(); }
      else if (e.key === "-") { setZoom(z => clamp(z - STEP)); e.preventDefault(); }
      else if (e.key === "0") { setZoom(1); e.preventDefault(); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const pct = Math.round(zoom * 100);
  const btn = {
    width: 32, height: 32, borderRadius: 999, border: "none",
    background: "transparent", cursor: "pointer",
    fontWeight: 900, color: "#5C5C66",
    display: "flex", alignItems: "center", justifyContent: "center",
  };

  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        position: "fixed", bottom: 16, right: 16, zIndex: 9999,
        display: "flex", alignItems: "center", gap: 2,
        background: "#fff",
        border: "1.5px solid #E6E6E9",
        borderRadius: 999,
        padding: 3,
        boxShadow: hover
          ? "0 10px 28px rgba(20,20,30,.18)"
          : "0 4px 12px rgba(20,20,30,.10)",
        opacity: hover ? 1 : 0.78,
        fontFamily: "'Archivo','-apple-system',sans-serif",
        transition: "opacity .15s, box-shadow .15s",
      }}>
      <button onClick={() => setZoom(z => clamp(z - STEP))}
        title="Zoom out (⌘−)" aria-label="Zoom out"
        style={{ ...btn, fontSize: 13 }}>A−</button>
      <button onClick={() => setZoom(1)}
        title="Reset to 100% (⌘0)" aria-label="Reset zoom"
        style={{
          minWidth: 44, padding: "0 8px", height: 32, borderRadius: 999,
          border: "none", background: pct === 100 ? "transparent" : "#F0F0F4",
          cursor: "pointer", fontSize: 11.5, fontWeight: 800,
          color: pct === 100 ? "#9A9AA2" : "#3D2E00",
          fontFamily: "'Space Mono',monospace",
        }}>{pct}%</button>
      <button onClick={() => setZoom(z => clamp(z + STEP))}
        title="Zoom in (⌘+)" aria-label="Zoom in"
        style={{ ...btn, fontSize: 16 }}>A+</button>
    </div>
  );
}
